/**
 * H2-F — Holder benefit detail (/buyer/benefits/:portfolioId/:associationKey).
 * Shows terms, limits, and redemption history. Redeeming = a request; only the
 * creator/provider confirms fulfilment.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import theme from '../theme';
import { BENEFIT_STATUS } from '../components/holder/BenefitList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const card = { background: '#fff', border: `1px solid ${theme.colors.borders}`, borderRadius: 12, padding: 20, marginBottom: 16 };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : null);
const TRANSFER = { stays_with_holder: 'Stays with you if the NFT is transferred', stays_with_nft: 'Moves with the NFT if it is transferred', non_transferable: 'Not transferable' };
const RED_STATUS = {
  requested: ['Requested — awaiting fulfilment', '#92400E'],
  fulfilled: ['Fulfilled — confirmed by the creator', '#065F46'],
  declined: ["Couldn't be fulfilled", '#991B1B'],
};
const Row = ({ label, value, testid }) => value == null || value === '' ? null : (
  <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderTop: `1px solid ${theme.colors.borders}`, fontSize: 13 }}>
    <div style={{ width: 180, color: theme.colors.text.muted, flexShrink: 0 }}>{label}</div>
    <div data-testid={testid} style={{ color: theme.colors.text.primary }}>{value}</div>
  </div>
);

function ActionArea({ b, onRedeem, busy, err, next, navigate }) {
  const msg = {
    locked: 'This benefit is locked until the requirements below are met.',
    scheduled: 'Not available yet — see when it opens below.',
    redeemed: "You've reached your usage limit for this benefit.",
    sold_out: 'All available redemptions have been used.',
    expired: 'This benefit has ended. The original terms are shown below for your records.',
    not_configured: "The creator hasn't finished setting this benefit up, so it can't be redeemed yet.",
  }[b.status];
  return (
    <div data-testid="benefit-detail-action" style={{ marginTop: 12 }}>
      {b.status === 'available' ? (
        <button data-testid="benefit-detail-redeem" onClick={onRedeem} disabled={busy}
          style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: theme.colors.primary, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
          {busy ? 'Requesting…' : 'Redeem / use benefit'}
        </button>
      ) : <div data-testid="benefit-detail-unavailable" style={{ fontSize: 13, color: theme.colors.text.secondary }}>{msg}</div>}
      {err && <div data-testid="benefit-detail-error" style={{ marginTop: 8, padding: 10, borderRadius: 8, background: '#FEF2F2', color: '#991B1B', fontSize: 13 }}>{err}</div>}
      {next.map(s => (
        <div key={s.code} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, marginTop: 8 }}>
          <span style={{ flex: 1 }}>{s.label}</span>
          {s.route && <button data-testid={`benefit-next-${s.code}`} onClick={() => navigate(s.route)} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${theme.colors.primary}`, background: '#fff', color: theme.colors.primary, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>Open</button>}
        </div>
      ))}
    </div>
  );
}

export default function BuyerBenefitDetailPage() {
  const { portfolioId, associationKey } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [actErr, setActErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const url = `${API}/buyer/benefits/${portfolioId}/${encodeURIComponent(associationKey)}`;

  const load = useCallback(async () => {
    try { setD((await axios.get(url, auth)).data); } catch (e) { setErr(e?.response?.data?.detail || 'Could not load this benefit'); }
  }, [url, auth]);
  useEffect(() => { load(); }, [load]);

  const redeem = async () => {
    setBusy(true); setActErr(null);
    try {
      await axios.post(`${API}/buyer/benefits/redeem`, { portfolio_id: portfolioId, association_key: associationKey, request_id: crypto.randomUUID() }, auth);
      await load();
    } catch (e) {
      const x = e?.response?.data?.detail;
      setActErr(typeof x === 'string' ? x : (x?.errors || []).join(' ') || 'Could not redeem');
    } finally { setBusy(false); }
  };

  const back = (
    <button data-testid="benefit-detail-back" onClick={() => navigate(-1)} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', background: 'none', border: 'none', color: theme.colors.primary, fontWeight: 700, cursor: 'pointer', marginBottom: 12 }}>
      <ArrowLeft size={14} /> Back
    </button>
  );
  if (err) return <div style={{ padding: 28, maxWidth: 860, margin: '0 auto' }}>{back}<div data-testid="benefit-detail-error-page" style={{ ...card, color: '#991B1B' }}>{err}</div>
    <button data-testid="benefit-detail-go-utilities" onClick={() => navigate('/buyer/utilities')} style={{ background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer' }}>Go to My Utilities</button></div>;
  if (!d) return <div data-testid="benefit-detail-loading" style={{ padding: 28 }}>Loading…</div>;

  const b = d.benefit;
  const st = BENEFIT_STATUS[b.status] || BENEFIT_STATUS.locked;
  return (
    <div data-testid="benefit-detail-page" style={{ padding: '24px 28px', maxWidth: 860, margin: '0 auto' }}>
      {back}
      <div style={card}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <h1 data-testid="benefit-detail-title" style={{ fontSize: 22, fontWeight: 900, flex: 1 }}>{b.title}</h1>
          <span data-testid="benefit-detail-status" style={{ padding: '3px 10px', borderRadius: 999, background: st.bg, color: st.color, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>{st.label}</span>
        </div>
        <div style={{ fontSize: 13, color: theme.colors.text.muted, marginTop: 2 }}>From <b>{d.creator_name}</b> · {d.portfolio.name}</div>
        {b.description && <p style={{ fontSize: 14, color: theme.colors.text.secondary, marginTop: 10 }}>{b.description}</p>}
        {(b.reasons || []).length > 0 && <div data-testid="benefit-detail-reasons" style={{ fontSize: 13, color: st.color, marginTop: 8 }}>{b.reasons.join(' · ')}</div>}
        <ActionArea b={b} onRedeem={redeem} busy={busy} err={actErr} next={d.next_steps} navigate={navigate} />
      </div>

      <div style={card}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Terms</div>
        {(b.conditions || []).length > 0 && (
          <div data-testid="benefit-detail-conditions" style={{ fontSize: 13, marginBottom: 8 }}>
            <div style={{ color: theme.colors.text.muted, marginBottom: 4 }}>{b.match === 'any' ? 'Unlocks when you meet any one of:' : 'Unlocks when you meet all of:'}</div>
            {b.conditions.map((c, i) => <div key={i} style={{ color: c.met ? '#065F46' : '#92400E' }}>{c.met ? '✓' : '○'} {c.label}</div>)}
          </div>
        )}
        <Row label="Available from" value={fmt(b.start_at)} />
        <Row label="Ends" value={fmt(b.expires_at)} />
        <Row label="Your uses" testid="benefit-detail-my-uses" value={b.per_holder_limit ? `${b.used_by_me} of ${b.per_holder_limit} used (${b.remaining_for_me} left)` : `${b.used_by_me} used · no personal limit`} />
        <Row label="Total remaining" value={b.total_limit ? `${b.total_remaining} of ${b.total_limit}` : null} />
        <Row label="Transfer" value={TRANSFER[b.transfer_behavior]} />
        <Row label="Fulfilled by" value={b.fulfillment_party} />
        <Row label="How to use it" value={b.fulfillment_instructions} />
        <Row label="Terms version" testid="benefit-detail-version" value={b.terms_version ? `v${b.terms_version}` : 'Not configured'} />
      </div>

      <div style={card} data-testid="benefit-detail-history">
        <div style={{ fontWeight: 800, marginBottom: 4 }}>Your redemptions</div>
        <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 8 }}>A redemption is a request. It's only marked fulfilled when the creator or provider confirms it.</div>
        {d.redemptions.length === 0 && <div style={{ fontSize: 13, color: theme.colors.text.muted }}>No redemptions yet.</div>}
        {d.redemptions.map(r => {
          const [label, color] = RED_STATUS[r.status] || [r.status, theme.colors.text.muted];
          return (
            <div key={r.id} data-testid={`benefit-redemption-${r.id}`} style={{ padding: '8px 0', borderTop: `1px solid ${theme.colors.borders}`, fontSize: 13 }}>
              <b style={{ color }}>{label}</b> · requested {fmt(r.redeemed_at)} · terms v{r.terms_version}
              {r.fulfilled_at && <span> · fulfilled {fmt(r.fulfilled_at)}</span>}
              {r.declined_at && <span> · {fmt(r.declined_at)}</span>}
              {r.creator_note && <div style={{ color: theme.colors.text.muted }}>Note from creator: {r.creator_note}</div>}
              <div style={{ fontSize: 11, color: theme.colors.text.muted }}>Reference {r.id}</div>
            </div>
          );
        })}
      </div>
      <button data-testid="benefit-detail-help" onClick={() => navigate('/buyer/help')} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer', fontSize: 13 }}>
        <HelpCircle size={14} /> Get help with this benefit
      </button>
    </div>
  );
}
